import React from 'react';
import { Box, Dialog, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { useTracking } from '../../../../context/TrackingContext';
import { SentryErrorBoundary } from '../../../../services/SentryService';
import { EventType } from '../../../../types/events';
import { getActionTypeDefinition } from '../../registry/actionTypeRegistry';
import { selectEditModalContext, selectIsEditModalOpen } from '../../store/selectors';
import { closeEditModal, setPendingCommand } from '../../store/slice';
import { ActionCommandType } from '../../types';

/**
 * Central edit modal component that renders different content
 * based on the action type using the registry pattern
 */
export const EditModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector(selectIsEditModalOpen);
  const context = useAppSelector(selectEditModalContext);

  // Analytics tracking
  const { trackEvent } = useTracking();

  const handleClose = () => {
    dispatch(closeEditModal());
  };

  const handleSave = (updatedPayload: Record<string, unknown>) => {
    if (!context) return;

    // Track edit event
    trackEvent(EventType.ACTION_EDITED, {
      action_id: context.action.action_id,
      action_type: context.actionType,
      encounter_id: context.action.encounter_id || null,
      patient_id: context.action.patient?.patient_id || null,
    });

    // Close the modal and save the pending command
    dispatch(closeEditModal());

    // Store the command to be executed after modal closes
    // This will be picked up by Actions.tsx
    dispatch(
      setPendingCommand({
        actionId: context.action.action_id,
        command: {
          type: ActionCommandType.ACCEPT_WITH_EDITS,
          payload: updatedPayload,
        },
      }),
    );
  };

  if (!isOpen || !context) {
    return null;
  }

  // Get appropriate edit component from registry
  const actionDefinition = getActionTypeDefinition(context.actionType);
  const EditComponent = actionDefinition?.editModal;

  if (!EditComponent) {
    console.error(`No edit modal registered for action type: ${context.actionType}`);
    return null;
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
     maxWidth={"md"} slotProps={{ paper: {
        sx: {
          borderRadius: 2,
          minHeight: '400px',
        },
      } }}>
      <SentryErrorBoundary
        fallback={
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error">An error occurred while loading the edit form.</Typography>
          </Box>
        }
      >
        <EditComponent
          context={context}
          onSave={handleSave}
          onCancel={handleClose}
          isLoading={false}
          error={null}
        />
      </SentryErrorBoundary>
    </Dialog>
  );
};
